/**
 * Platzhalter-Hilfe für E-Mail-Trigger
 *
 * Enthält für jeden Trigger einen Hilfe-Content (HTML),
 * der im Info-Modal angezeigt wird.
 *
 * Der Content wird dynamisch aus den mail_context-Funktionen der Trigger generiert,
 * damit die angezeigten Platzhalter immer mit dem Backend-Code übereinstimmen.
 */
const helpContent = {
  newEmploye: {
    title: "Neuer Mitarbeiter angelegt",
    description: "Wird gesendet, wenn ein neuer Mitarbeiter angelegt wurde.",
    placeholders: [
      {
        name: "{mitarbeiterId}",
        description: "ID des Mitarbeiters",
        recipient: "Alle",
      },
      {
        name: "{mitarbeiterfirmenId}",
        description: "Firmen-ID des Mitarbeiters",
        recipient: "Alle",
      },
      {
        name: "{mitarbeitername}",
        description: "Vor- und Nachname des Mitarbeiters",
        recipient: "Alle",
      },
      {
        name: "{mitarbeitergeburtstag}",
        description: "Geburtstag des Mitarbeiters",
        recipient: "Alle",
      },
      {
        name: "{mitarbeiteremail}",
        description: "E-Mail-Adresse des Mitarbeiters",
        recipient: "Alle",
      },
      {
        name: "{untersuchungenbg_grundsatz}",
        description: "BG-Grundsatz der Untersuchung",
        recipient: "Alle",
      },
      {
        name: "{untersuchungenanlass}",
        description: "Anlass der Untersuchung",
        recipient: "Alle",
      },
      {
        name: "{untersuchungentitel}",
        description: "Titel der Untersuchung",
        recipient: "Alle",
      },
      {
        name: "{untersuchungenId}",
        description: "ID der Untersuchung",
        recipient: "Alle",
      },
      {
        name: "{untersuchungenart}",
        description: "Art der Untersuchung",
        recipient: "Alle",
      },
      {
        name: "{untersuchungenablaufdatum}",
        description: "Ablaufdatum / nächster Termin",
        recipient: "Alle",
      },
    ],
    functions: [
      {
        syntax: "<list>...</list>",
        description:
          "Erstellt eine Liste aller zugeordneten Untersuchungen. Platzhalter innerhalb der Tags werden pro Untersuchung ersetzt.",
      },
    ],
    example: `Hallo {mitarbeitername},

folgende Untersuchungen wurden Ihnen zugeordnet:
<list>
• {untersuchungentitel} (BG: {untersuchungenbg_grundsatz})
</list>

Mit freundlichen Grüßen
Ihr DiVoKa-Team`,
  },

  comingUpExamination: {
    title: "Untersuchungen stehen an",
    description:
      "Cron-gesteuerte Benachrichtigung über anstehende Untersuchungen.",
    placeholders: [
      {
        name: "{mitarbeitername}",
        description: "Vor- und Nachname des Mitarbeiters",
        recipient: "Mitarbeiter",
      },
      {
        name: "{untersuchung-list}",
        description: "HTML-Tabelle mit allen anstehenden Untersuchungen",
        recipient: "Alle",
      },
      {
        name: "{ma-list}",
        description: "HTML-Liste aller betroffenen Mitarbeiter",
        recipient: "Individuell",
      },
    ],
    functions: [],
    example: `Beispiel (Empfänger: Mitarbeiter):
Hallo {mitarbeitername},

folgende Untersuchungen stehen an:
{untersuchung-list}

---

Beispiel (Empfänger: Individuell):
Sehr geehrte Damen und Herren,

folgende Mitarbeiter haben anstehende Untersuchungen:
{ma-list}

Details:
{untersuchung-list}`,
  },

  angebotExamination: {
    title: "Angebot Vorsorge automatisches Ergebnis",
    description:
      "Sendet eine Erinnerung für anstehende Angebotsuntersuchungen und generiert ein automatisches Ergebnis nach dem Versand.",
    placeholders: [
      {
        name: "{mitarbeitername}",
        description: "Vor- und Nachname des Mitarbeiters",
        recipient: "Mitarbeiter",
      },
      {
        name: "{untersuchung-list}",
        description:
          "HTML-Tabelle mit allen anstehenden Angebotsuntersuchungen",
        recipient: "Mitarbeiter",
      },
    ],
    functions: [],
    example: `Hallo {mitarbeitername},

folgende Angebotsuntersuchungen stehen an:
{untersuchung-list}

Mit freundlichen Grüßen
Ihr DiVoKa-Team`,
  },

  bereichExamination: {
    title: "Untersuchung steht an in Bereich",
    description:
      "Benachrichtigung an Vorgesetzte von Tätigkeitsbereichen, wenn Untersuchungen für Mitarbeiter in ihrem Bereich anstehen.",
    placeholders: [
      {
        name: "{bereichname}",
        description: "Name des Tätigkeitsbereichs (auch im Betreff verwendbar)",
        recipient: "Vorgesetzte",
      },
      {
        name: "{ma-list}",
        description: "HTML-Liste aller betroffenen Mitarbeiter im Bereich",
        recipient: "Vorgesetzte",
      },
    ],
    functions: [],
    example: `Betreff: Anstehende Untersuchungen — {bereichname}

Sehr geehrte Damen und Herren,

in Ihrem Bereich stehen folgende Untersuchungen an:
{ma-list}

Mit freundlichen Grüßen
Ihr DiVoKa-Team`,
  },
};

/**
 * Generiert den HTML-Content für das Hilfe-Modal eines bestimmten Triggers.
 * @param {string} triggerKey - Der Key des Triggers (z.B. 'comingUpExamination')
 * @returns {string} HTML-String für das Modal
 */
function getPlaceholderHelpHtml(triggerKey, metaFields = []) {
  const help = helpContent[triggerKey];
  if (!help) {
    return (
      "<p>Keine Hilfe für diesen Auslöser verfügbar.</p>" +
      metaSectionHtml(metaFields)
    );
  }

  let html = "";

  // Beschreibung
  html += `<div class="help-description"><i class="fas fa-info-circle"></i>${help.description}</div>`;

  // Platzhalter-Section
  if (help.placeholders && help.placeholders.length > 0) {
    html += `<div class="help-section">`;
    html += `<div class="section-title"><i class="fas fa-code"></i>Verfügbare Platzhalter</div>`;
    html += `<table class="help-table">`;
    html += `<thead><tr><th class="help-col-placeholder">Platzhalter</th><th class="help-col-desc">Beschreibung</th><th class="help-col-recipient">Empfänger</th></tr></thead>`;
    html += `<tbody>`;
    for (const ph of help.placeholders) {
      html += `<tr>`;
      html += `<td><code>${ph.name}</code></td>`;
      html += `<td>${ph.description}</td>`;
      html += `<td><span class="badge badge-light">${ph.recipient}</span></td>`;
      html += `</tr>`;
    }
    html += `</tbody></table>`;
    html += `</div>`;
  }

  // Metafelder
  html += metaSectionHtml(metaFields);

  // Funktionen
  if (help.functions && help.functions.length > 0) {
    html += `<div class="help-section">`;
    html += `<div class="section-title"><i class="fas fa-cogs"></i>Funktionen</div>`;
    html += `<table class="help-table">`;
    html += `<thead><tr><th class="help-col-syntax">Syntax</th><th class="help-col-desc">Beschreibung</th></tr></thead>`;
    html += `<tbody>`;
    for (const fn of help.functions) {
      html += `<tr>`;
      html += `<td><code>${fn.syntax}</code></td>`;
      html += `<td>${fn.description}</td>`;
      html += `</tr>`;
    }
    html += `</tbody></table>`;
    html += `</div>`;
  }

  // Beispiele
  if (help.example) {
    html += `<div class="help-section">`;
    html += `<div class="section-title"><i class="fas fa-lightbulb"></i>Beispiele</div>`;
    html += `<div class="help-examples">`;

    // Split examples by "---" separator
    const examples = help.example
      .split("---")
      .map((e) => e.trim())
      .filter((e) => e);

    for (let i = 0; i < examples.length; i++) {
      const example = examples[i];
      // Extract title from first line if it starts with "Beispiel"
      const lines = example.split("\n");
      let title = `Beispiel ${i + 1}`;
      let content = example;

      if (lines[0].includes("Beispiel")) {
        title = lines[0]
          .replace("Beispiel", "")
          .replace(":", "")
          .replace("(", "")
          .replace(")", "")
          .trim();
        if (title.startsWith("Empfänger")) {
          title = `Beispiel (${title})`;
        }
        content = lines.slice(1).join("\n").trim();
      }

      html += `<div class="example-card">`;
      html += `<div class="example-header"><i class="fas fa-envelope"></i>${title}</div>`;
      html += `<div class="example-body"><pre>${escapeHtml(content)}</pre></div>`;
      html += `</div>`;
    }

    html += `</div></div>`;
  }

  return html;
}

/**
 * Metafelder-Sektion ({meta_<key>}) — nur canMail=1-Felder, Shadowing-Dedupe
 * passiert beim Laden (MailForm.loadMetaKeys)
 */
function metaSectionHtml(metaFields) {
  if (!metaFields || metaFields.length === 0) return "";
  let html = `<div class="help-section">`;
  html += `<div class="section-title"><i class="fas fa-database"></i>Metafelder</div>`;
  html += `<table class="help-table">`;
  html += `<thead><tr><th class="help-col-placeholder">Platzhalter</th><th class="help-col-desc">Feld</th></tr></thead>`;
  html += `<tbody>`;
  for (const f of metaFields) {
    html += `<tr>`;
    html += `<td><code>{meta_${f.key}}</code></td>`;
    html += `<td>${escapeHtml(f.label)}</td>`;
    html += `</tr>`;
  }
  html += `</tbody></table>`;
  html += `</div>`;
  return html;
}

/**
 * Escapes HTML special characters
 * @param {string} text
 * @returns {string}
 */
function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export { helpContent, getPlaceholderHelpHtml };
